import { useMemo, useEffect } from 'react';
import { useReactTable, getCoreRowModel, flexRender, type ColumnDef } from '@tanstack/react-table';
import { web as webRoute } from '@/support/route';
import {
  allAnimalVariablesExcluded,
  formatAverage,
  getExcludedIncludedMap,
  getExcludedOrIncludedVariableApiIds,
  getVariableApiIdsFromMetrics,
} from './Groups.utils';
import GroupLabel from '@/components/UI/GroupLabel/GroupLabel';
import Link from '@/components/UI/Link';
import { formatNumber, objectDiff, successToast } from '@/helpers';
import { _isNil, _isNotEmpty, _notNil } from '@/littledash';
import IncludeExcludeVariablesButton from '@/components/UI/IncludeExcludeVariablesButton';
import './GroupsTable.scss';
import type { ID } from '@/model/Common.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import type { Study } from '@/model/Study.model';
import type { VariableApiId } from '@/model/Measurement.model';
import Button from '@/components/UI/Button/Button';
//@ts-expect-error - old hook form - replace with `react-hook-form@latest`
import { useWatch, useForm, useFormContext, FormProvider } from 'react-hook-form';
import * as Auth from '@/support/auth';
import classNames from 'classnames';

interface GroupTableProps {
  metrics: any[];
  group: any;
  study: Study;
  initialValues: Record<VariableApiId, boolean>;
}

interface AnimalCellProps {
  studyId: ID;
  animalId: ID;
  animalName: string;
  animalVariableApiIds: VariableApiId[];
  isWriteUser?: boolean;
}

interface DataCellProps {
  value: string | null;
  variableApiId: VariableApiId;
  isWriteUser?: boolean;
  initialValues: GroupTableProps['initialValues'];
}

interface TableButtonActionsProps {
  onSubmit: (formData: Record<VariableApiId, boolean>) => Promise<void>;
  onReset: () => Promise<void>;
  isSubmitting: boolean;
  isResetting: boolean;
  initialValues: Record<VariableApiId, boolean>;
}

const GroupsTables = ({
  metrics,
  study,
  fetchMetrics,
}: {
  metrics: any[];
  study: Study;
  fetchMetrics: () => Promise<void>;
}) => {
  const initExcludedMap = useMemo(() => getExcludedIncludedMap(metrics), [metrics]);
  const formMethods = useForm({
    defaultValues: initExcludedMap,
  });
  const { reset } = formMethods;
  const { invoke: includeVariables, loading: includeVariablesUpdating } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyId}/variables-include',
    invokeOnInit: false,
  });
  const { invoke: excludeVariables, loading: excludeVariablesUpdating } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyId}/variables-exclude',
    invokeOnInit: false,
  });

  useEffect(() => {
    reset(getExcludedIncludedMap(metrics));
  }, [metrics, reset]);

  const onResetVariables = async (): Promise<void> => {
    const includes: VariableApiId[] = [];
    for (const variableApiId in initExcludedMap) {
      if (initExcludedMap?.[variableApiId as VariableApiId] === true) {
        includes.push(variableApiId as VariableApiId);
      }
    }
    if (_isNotEmpty(includes)) {
      await includeVariables({
        path: { studyId: study.api_id },
        body: { variable_api_ids: includes },
      });
      await fetchMetrics();
      successToast('Reset excluded measurements');
    }
  };

  const onSubmitChanges = async (formData: Record<VariableApiId, boolean>): Promise<void> => {
    const formDiffs = objectDiff(initExcludedMap, formData);
    const excludes: VariableApiId[] = [];
    const includes: VariableApiId[] = [];
    for (const variableApiId in formDiffs) {
      const diff = formDiffs?.[variableApiId]?.obj2;
      if (diff === true) {
        excludes.push(variableApiId as VariableApiId);
      }
      if (diff === false) {
        includes.push(variableApiId as VariableApiId);
      }
    }
    if (_isNotEmpty(excludes)) {
      await excludeVariables({
        path: { studyId: study.api_id },
        body: { variable_api_ids: excludes },
      });
      await fetchMetrics();
    }
    if (_isNotEmpty(includes)) {
      await includeVariables({
        path: { studyId: study.api_id },
        body: { variable_api_ids: includes },
      });
      await fetchMetrics();
    }
    successToast('Updated measurements');
  };

  return (
    <div>
      <FormProvider {...formMethods}>
        {Auth.isWriteUserForStudy(study) && (
          <TableButtonActions
            onSubmit={onSubmitChanges}
            onReset={onResetVariables}
            isSubmitting={includeVariablesUpdating}
            isResetting={excludeVariablesUpdating}
            initialValues={initExcludedMap}
          />
        )}
        {metrics.map((group) => (
          <div key={group.api_id} className="bt odd:b--moon-gray" data-testid={`graphs__tables--entry:${group.id}`}>
            <GroupTable
              key={`group-table-${group?.id}`}
              metrics={metrics}
              group={group}
              study={study}
              initialValues={initExcludedMap}
            />
          </div>
        ))}
      </FormProvider>
    </div>
  );
};

const TableButtonActions = ({
  onSubmit,
  onReset,
  isSubmitting,
  isResetting,
  initialValues,
}: TableButtonActionsProps): JSX.Element => {
  const { handleSubmit, control } = useFormContext();
  const currentValues = useWatch({ control });
  const diffs = objectDiff(initialValues, currentValues);
  const notTouched = Object.keys(diffs).length === 0;
  const hasNoInitialExcluded = getExcludedOrIncludedVariableApiIds(initialValues, true).length === 0;

  return (
    <div className="flex justify-end pa3 bt b--moon-gray">
      <Button plain onClick={() => onReset()} disabled={isSubmitting || hasNoInitialExcluded}>
        Reset
      </Button>
      <Button className="ml3" onClick={handleSubmit(onSubmit)} disabled={isResetting || notTouched}>
        Submit changes
      </Button>
    </div>
  );
};

const TABLE_FOOTER_CLASSES = 'db fw3 near-black lh-copy';

const GroupTable = ({ metrics, group, study, initialValues }: GroupTableProps) => {
  const isWriteUser = Auth.isWriteUserForStudy(study);

  const averageTypes = ['sd', 'sem', 'mad'];

  const dayColumns = metrics
    .reduce((days: any[], group: any) => {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (group.animals) {
        group.animals.forEach((animal: any) => {
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          if (animal.values) {
            animal.values.forEach(({ study_day: studyDay }: any) => !days.includes(studyDay) && days.push(studyDay));
          }
        });
      }
      return days;
    }, [])
    .sort((a: any, b: any) => Number(a) - Number(b))
    .map((day: any) => {
      const averages = group.averages.find((average: any) => average.study_day === day);

      const metricValue = (method: any, metrics: any, spaceTop: boolean) => (
        <span key={method} className={`${TABLE_FOOTER_CLASSES} ${method} ${spaceTop ? 'mt2' : ''}`}>
          {averageTypes.includes(method) ? '±' : ''}
          {formatAverage(metrics)}
        </span>
      );

      return {
        id: `day-${day}`,
        header: () => `Day ${day}`,
        accessorFn: (animal: any) => {
          const variable = animal?.values?.find((a: any) => a.study_day === day);
          return variable;
        },
        cell: ({ getValue }: any) => {
          const variable = getValue();
          const apiId = variable?.api_id;
          const value = _notNil(variable?.value) ? String(formatNumber(variable.value)) : null;
          if (_notNil(value) && _notNil(apiId)) {
            return (
              <DataCell value={value} variableApiId={apiId} isWriteUser={isWriteUser} initialValues={initialValues} />
            );
          }
          return <>{value}</>;
        },
        footer: () =>
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          averages ? (
            <>
              {metricValue('mean', averages.mean, false)}
              {metricValue('sd', averages.sd, false)}
              {metricValue('sem', averages.sem, false)}
              {metricValue('median', averages.median, true)}
              {metricValue('mad', averages.mad, false)}
              {metricValue('dtdc', averages.dtdc, true)}
              {metricValue('tgi', averages.tgi, false)}
            </>
          ) : null,
        meta: {
          className: 'ui__day-column',
        },
      } as ColumnDef<any, unknown>;
    });

  const columns: ColumnDef<any, unknown>[] = [
    {
      id: 'group-label',
      accessorKey: 'group.name',
      header: () => <GroupLabel group={group} />,
      footer: () => (
        <div>
          <span className={`${TABLE_FOOTER_CLASSES} mean`}>Mean</span>
          <span className={`${TABLE_FOOTER_CLASSES} sd`}>SD</span>
          <span className={`${TABLE_FOOTER_CLASSES} sem`}>SEM</span>
          <span className={`${TABLE_FOOTER_CLASSES} mt2 median`}>Median</span>
          <span className={`${TABLE_FOOTER_CLASSES} mad`}>MAD</span>
          <span className={`${TABLE_FOOTER_CLASSES} mt2 dtdc`}>% dT/dC</span>
          <span className={`${TABLE_FOOTER_CLASSES} tgi`}>% Inhibition</span>
        </div>
      ),
      size: 175,
      meta: {
        isFixed: true,
        className: 'ui__fixed-column ui__group-column',
      },
    },
    {
      id: 'number',
      accessorKey: 'number',
      header: () => 'No.',
      size: 50,
      meta: {
        isFixed: true,
        className: 'ui__fixed-column ui__number-column',
      },
    },
    {
      id: 'animal-name',
      accessorKey: 'name',
      header: () => 'ID',
      size: 150,
      cell: ({ row }: any) => {
        const animalId = row.original?.id;
        const animalName = row.original?.name;
        const animalVariableApiIds = getVariableApiIdsFromMetrics(animalId, metrics);
        if (_isNil(animalId)) {
          return <div>-</div>;
        }
        return (
          <AnimalCell
            studyId={study.id}
            animalId={animalId}
            animalName={animalName}
            animalVariableApiIds={animalVariableApiIds}
            isWriteUser={isWriteUser}
          />
        );
      },
      meta: {
        isFixed: true,
        className: 'ui__fixed-column ui__animal-column',
      },
    },
    ...dayColumns,
  ];

  // TanStack Table v8 configuration
  const table = useReactTable({
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    data: group.animals || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    enableColumnResizing: false,
    enableFilters: false,
  });

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const noData = !group.animals || group.animals.length === 0;

  return (
    <div className="ui__groups-table -striped" key={group.name}>
      {/* Header */}
      <div className="rt-thead -header">
        {table.getHeaderGroups().map((headerGroup) => (
          <div key={headerGroup.id} className="rt-tr">
            {headerGroup.headers.map((header) => {
              const meta = header.column.columnDef.meta as any;
              return (
                <div
                  key={header.id}
                  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                  className={`rt-th ${meta?.className || ''}`}
                  style={{
                    width: header.getSize(),
                    minWidth: header.getSize(),
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    maxWidth: meta?.isFixed ? header.getSize() : undefined,
                  }}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Body */}
      <div className="rt-tbody">
        {noData ? (
          <div className="rt-tr-group">
            <div className="rt-tr -odd">
              <div className="rt-td" style={{ textAlign: 'center', padding: '20px' }}>
                No measurements yet
              </div>
            </div>
          </div>
        ) : (
          table.getRowModel().rows.map((row, index) => (
            <div key={row.id} className="rt-tr-group">
              <div className={`rt-tr ${index % 2 === 0 ? '-even' : '-odd'}`}>
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta as any;
                  return (
                    <div
                      key={cell.id}
                      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                      className={`rt-td ${meta?.className || ''}`}
                      style={{
                        width: cell.column.getSize(),
                        minWidth: cell.column.getSize(),
                        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                        maxWidth: meta?.isFixed ? cell.column.getSize() : undefined,
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="rt-tfoot">
        {table.getFooterGroups().map((footerGroup) => (
          <div key={footerGroup.id} className="rt-tr">
            {footerGroup.headers.map((header) => {
              const meta = header.column.columnDef.meta as any;
              return (
                <div
                  key={header.id}
                  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                  className={`rt-td ${meta?.className || ''}`}
                  style={{
                    width: header.getSize(),
                    minWidth: header.getSize(),
                    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                    maxWidth: meta?.isFixed ? header.getSize() : undefined,
                  }}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

const AnimalCell = ({
  studyId,
  animalId,
  animalName,
  animalVariableApiIds,
  isWriteUser = false,
}: AnimalCellProps): JSX.Element => {
  const { setValue } = useFormContext();
  // this will isolate re-rendering at the custom hook level don't swap for `watch`
  const variableState: Record<VariableApiId, boolean> = useWatch({ name: animalVariableApiIds });
  const allVariablesExcluded = allAnimalVariablesExcluded(variableState);

  const handleIncludeExcludeClick = (): void => {
    let toExclude = false;
    if (!allVariablesExcluded) {
      toExclude = true;
    }

    for (const variableKey in variableState) {
      setValue(variableKey, toExclude);
    }
  };

  return (
    <div className="w-100 hide-child">
      <div className="flex justify-between items-center relative">
        <Link
          to={webRoute('animals.show', {
            id: studyId,
            subject_id: animalId,
          })}
          className="link blue"
        >
          {animalName}
        </Link>
        {isWriteUser && (
          <div className="include-exclude-variable-button child absolute right-0 z-2">
            <IncludeExcludeVariablesButton isExcluded={allVariablesExcluded} onClick={handleIncludeExcludeClick} />
          </div>
        )}
      </div>
    </div>
  );
};

/** `<Controller />` with hook-form V6 has an infinite re-render issue with react table v6 cells
 *  Instead we need to register this 'form-field' seperately and isolate the re-render
 */

const DataCell = ({ value, variableApiId, isWriteUser = false, initialValues }: DataCellProps): JSX.Element => {
  const { register, setValue } = useFormContext();

  // this will isolate re-rendering at the custom hook level don't swap for `watch`
  const excluded = useWatch({ name: variableApiId });
  const initialExcluded = initialValues?.[variableApiId] === true;

  useEffect(() => {
    // we need to custom register all variables for the `handleSubmit` and `useWatch` values
    register(variableApiId);
  }, [register, variableApiId]);

  const handleClick = (): void => {
    if (isWriteUser) {
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      setValue(variableApiId, !excluded);
    }
  };

  const className = classNames({
    pointer: isWriteUser,
    blue: initialExcluded,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    strike: excluded || (initialExcluded && excluded),
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    'strike-hover': !excluded || !initialExcluded,
  });

  return (
    <div onClick={handleClick} className={className}>
      {value}
    </div>
  );
};

export default GroupsTables;
